import React, {SyntheticEvent, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {differenceInDays} from 'date-fns/differenceInDays'
import {
  RecentSearchParams,
  useSearchBox,
  useSearchBoxDatePicker,
  useSearchBoxDestinationPicker,
  useSearchBoxGuestPicker
} from 'hooks/searchBox'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {
  getSearchParameters,
  getSearchParametersForSearchBox
} from 'modules/sapiSearch/selectors'
import {areSapiSearchParamsValid} from 'modules/sapiSearch/utils'
import {
  getIsUserChangedDates,
  getSearchBoxValues
} from 'modules/searchBox/selectors'
import {setOpenDatePickerType} from 'modules/searchBox/slice'
import {SearchTrigger} from 'modules/searchBox/types'

import OpacityAnimation from '@vio/atlas/helpers/OpacityAnimation'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {
  roomsSplitToString,
  roomsToConfigurationObject
} from '@vio/core/src/room/business/roomConfiguration'
import {RoomSplitType} from '@vio/core/src/room/types/RoomConfiguration'

import {SearchFormDesktop} from './SearchFormDesktop'

const StyledOpacityAnimation = styled(OpacityAnimation)`
  flex: 1 auto;
  display: contents;
`

export const SearchBoxDesktop = ({
  showDateConfirmationTooltip = false
}: {
  showDateConfirmationTooltip?: boolean
}) => {
  const dispatch = useDispatch()
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const searchParameters = useSelector(getSearchParameters)
  const isUserChangedDates = useSelector(getIsUserChangedDates)
  const initialDatePickerValues = {
    checkIn: searchParameters?.checkIn,
    checkOut: searchParameters?.checkOut
  }

  const initialGuestPickerValues = {
    rooms: searchParameters?.rooms,
    optimizeRooms: searchParameters?.optimizeRooms
  }

  const {doSearch} = useSearchBox()

  const sapiSearchParamsAreValid = areSapiSearchParamsValid(searchParameters)
  const {checkIn, checkOut, rooms, optimizeRooms} =
    useSelector(getSearchBoxValues)
  const {
    checkIn: initialCheckIn,
    checkOut: initialCheckOut,
    rooms: initialRooms,
    optimizeRooms: initialOptimizeRooms
  } = useSelector(getSearchParametersForSearchBox)

  const shouldHideDate = useCallback(
    (newDate: string, defaultDate: string) => {
      if (!isDatelessSeoSRP) return false
      return differenceInDays(newDate, defaultDate) === 0 && !isUserChangedDates
    },
    [isDatelessSeoSRP, isUserChangedDates]
  )

  const {numberOfAdults, numberOfChildren, numberOfRooms, roomsSplit} =
    roomsToConfigurationObject(rooms)

  const {
    onDateChange,
    onDatePickerClose,
    onDatePickerDayClick,
    onDatePickerOpen,
    onDatePickerInputClick,
    onDatePickerReset
  } = useSearchBoxDatePicker(initialDatePickerValues)

  const {
    onRecentSearchSelected,
    destinationPickerInputDisplayValue,
    destinationDisplayValue,
    onDestinationPickerInputClick,
    onDestinationChange,
    setDestinationHasError,
    setDestinationErrorMessage,
    onSuggestionsRequested,
    onNearbyPropertiesSuggestionSelected
  } = useSearchBoxDestinationPicker()

  const handleRecentSearchSelected = useCallback(
    (recentSearch: RecentSearchParams): void => {
      onRecentSearchSelected({recentSearch})
    },
    [onRecentSearchSelected]
  )

  const handleNearbyPropertiesSuggestionSelected = useCallback((): void => {
    onNearbyPropertiesSuggestionSelected()
    dispatch(setOpenDatePickerType(DatePickerType.CheckIn))
  }, [dispatch, onNearbyPropertiesSuggestionSelected])

  const handleSuggestionSelected = useCallback((): void => {
    dispatch(setOpenDatePickerType(DatePickerType.CheckIn))
  }, [dispatch])

  const handleSubmit = useCallback(
    (event?: SyntheticEvent | null | undefined): void | boolean => {
      event?.preventDefault()
      if (!sapiSearchParamsAreValid) {
        setDestinationHasError(true)
        setDestinationErrorMessage(
          destinationDisplayValue
            ? 'destinationPickerUnknownMessage'
            : 'destinationPickerEmptyMessage'
        )
        return false
      }

      doSearch({
        searchTrigger: SearchTrigger.Submit
      })
    },
    [
      destinationDisplayValue,
      doSearch,
      setDestinationErrorMessage,
      setDestinationHasError,
      sapiSearchParamsAreValid
    ]
  )

  const {
    onGuestRoomsChange,
    onGuestOptimizeRoomsChange,
    onGuestPickerReset,
    onGuestPickerClick,
    onGuestPickerOptionInfoDisplayed,
    onGuestConfigPreferenceClick
  } = useSearchBoxGuestPicker(initialGuestPickerValues)

  const handleGuestChange = useCallback(
    (roomsSplit: RoomSplitType[]): void => {
      const rooms = roomsSplitToString(roomsSplit)
      onGuestRoomsChange(rooms)
    },
    [onGuestRoomsChange]
  )

  const handleGuestOptimizeRoomsChange = useCallback(
    (isOptimizeRooms: boolean): void => {
      onGuestOptimizeRoomsChange(isOptimizeRooms)
    },
    [onGuestOptimizeRoomsChange]
  )

  return (
    <StyledOpacityAnimation>
      <SearchFormDesktop
        showDateConfirmationTooltip={showDateConfirmationTooltip}
        roomsSplit={roomsSplit}
        optimizeRooms={Boolean(optimizeRooms)}
        initialRooms={initialRooms}
        initialOptimizeRooms={initialOptimizeRooms}
        destinationPickerInputDisplayValue={destinationPickerInputDisplayValue}
        destinationDisplayValue={destinationDisplayValue}
        numberOfAdults={numberOfAdults}
        numberOfChildren={numberOfChildren}
        numberOfRooms={numberOfRooms}
        onDatePickerInputClick={onDatePickerInputClick}
        onDestinationPickerInputClick={onDestinationPickerInputClick}
        onGuestPickerClick={onGuestPickerClick}
        onSuggestionSelected={handleSuggestionSelected}
        onDestinationChange={onDestinationChange}
        onRecentSearchSelected={handleRecentSearchSelected}
        onSuggestionsRequested={onSuggestionsRequested}
        onDateChange={onDateChange}
        onDatePickerDayClick={onDatePickerDayClick}
        onDatePickerOpen={onDatePickerOpen}
        onSubmit={handleSubmit}
        onGuestRoomsChange={handleGuestChange}
        onGuestOptimizeRoomsChange={handleGuestOptimizeRoomsChange}
        onGuestPickerReset={onGuestPickerReset}
        onDatePickerClose={onDatePickerClose}
        onDatePickerReset={onDatePickerReset}
        onNearbyPropertiesClick={handleNearbyPropertiesSuggestionSelected}
        onGuestPickerInfoDisplayed={onGuestPickerOptionInfoDisplayed}
        onGuestConfigPreferenceClick={onGuestConfigPreferenceClick}
        {...(shouldHideDate(checkIn, initialCheckIn)
          ? {initialCheckIn: undefined, checkIn: undefined}
          : {initialCheckIn, checkIn})}
        {...(shouldHideDate(checkOut, initialCheckOut)
          ? {initialCheckOut: undefined, checkOut: undefined}
          : {initialCheckOut, checkOut})}
      />
    </StyledOpacityAnimation>
  )
}
